import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Brain,
  Cpu,
  RefreshCw,
  Wand2,
} from 'lucide-react';
import { Block, TradingPlatform, AssetClass } from '../../types';
import { callEdgeFunction } from '../../utils/supabaseFunctions';
import { track } from '../../utils/analytics';
import { useBlockLibraryGate } from '../../config/useBlockLibraryGate';
import { resolveStrategyLibraryRefs } from '../../services/aiCatalogClient';
// P9.2B — structural research generation (the DEFAULT): proposition picker data +
// typed-rejection vocabulary. Titles/status only; never card prose.
import ideaBankSummary from '../../generated/ideaBankSummary.json';

interface AIStrategyBuilderProps {
  onStrategyGenerated: (blocks: Block[]) => void;
}

// ─── Auto-layout (M14) ──────────────────────────────────────────────────────
// Column-by-type layout so the canvas reads left → right by data flow,
// regardless of whatever positions the AI emitted. Pure function — clones
// the blocks; never mutates input.
const COLUMN_X: Record<string, number> = {
  strategy_config:       80,
  price_data:           320,
  moving_average:       320,
  momentum_oscillator:  320,
  volatility:           320,
  volume:               320,
  trend:                320,
  chart_patterns:       320,
  candlestick_patterns: 320,
  time_filter:          320,
  custom:               320,
  expression:           600,
  logic_gate:           600,
  entry:                880,
  exit:                 880,
  stop_loss:            880,
  take_profit:          880,
  // P9.2B — structural setup blocks
  setup_event:          320,
  setup_state:          600,
};

function autoLayoutBlocks(blocks: Block[]): Block[] {
  if (!Array.isArray(blocks) || blocks.length === 0) return blocks;
  // Bucket by column, preserve relative order within a column (stable layout
  // means the AI's intent is still reflected in the vertical ordering).
  const byColumn = new Map<number, Block[]>();
  for (const b of blocks) {
    const x = COLUMN_X[b.type] ?? 600;
    if (!byColumn.has(x)) byColumn.set(x, []);
    byColumn.get(x)!.push(b);
  }
  const COL_SPACING = 160;
  const TOP = 80;
  const out: Block[] = [];
  for (const b of blocks) {
    const x = COLUMN_X[b.type] ?? 600;
    const col = byColumn.get(x)!;
    const idx = col.indexOf(b);
    out.push({ ...b, position: { x, y: TOP + idx * COL_SPACING } });
  }
  return out;
}

const AIStrategyBuilder: React.FC<AIStrategyBuilderProps> = ({
  onStrategyGenerated,
}) => {
  const libGate = useBlockLibraryGate();
  const [description, setDescription] = useState('');
  // Platform + Asset class are vestigial here for the same reason they
  // were on the AI Optimize panel — the AI infers asset class from the
  // strategy description, and Platform has no material differentiation
  // in the generation prompt. Held as fixed constants so the
  // generate-strategy edge-function contract is unchanged.
  const platform: TradingPlatform = TradingPlatform.MT5;
  const assetClass: AssetClass = AssetClass.FOREX;
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The precise structural-validation failures the function returns (it
  // surfaces them in the 422 body but the UI used to throw them away).
  const [errorDetails, setErrorDetails] = useState<string[] | null>(null);
  // Metadata about the last generation — surfaced so users can see which
  // model produced the strategy, how long it took, and roughly what it cost.
  interface GenMeta {
    model: string;
    attempts: number;
    latency_ms: number;
    tokens?: { input?: number; output?: number };
    /** Wall-clock at the moment of generation. Used to render "x minutes ago". */
    completedAt?: number;
    // P15 — how the strategy was generated: plain JSON, TSS by manual
    // toggle, or TSS auto-retry after a JSON timeout. Surfaced + tracked.
    generationMode?: 'json' | 'tss_manual' | 'tss_retry_after_json_timeout';
    /** P15 — server compiled the TSS but fell back to JSON internally. */
    scriptFellBack?: boolean;
  }
  // P15 — opt-in TSS for the Builder's from-scratch generation. Default OFF
  // (beta; JSON stays the default first attempt). When on, the first attempt
  // uses TSS. Regardless, a JSON timeout (504) auto-retries once in TSS.
  const [useScript, setUseScript] = useState(false);
  const META_STORAGE_KEY = 'tradeatory.ai.lastMeta';
  const [lastMeta, setLastMeta] = useState<GenMeta | null>(() => {
    // Lazy initialiser: pull persisted meta on first mount so the panel
    // survives navigation away from the AI tab (parent auto-switches to
    // the canvas after onStrategyGenerated fires).
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(META_STORAGE_KEY) : null;
      return raw ? (JSON.parse(raw) as GenMeta) : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (lastMeta) window.localStorage.setItem(META_STORAGE_KEY, JSON.stringify(lastMeta));
    else window.localStorage.removeItem(META_STORAGE_KEY);
  }, [lastMeta]);

  // Strategy-archetype presets — same shape and content as the AI Optimize
  // panel's FROM_SCRATCH_PRESETS so the two from-scratch entry points feel
  // consistent. Asset-agnostic on purpose: the user names their target
  // market inside the description if they care, and the AI infers default
  // asset class from there.
  const PRESETS: Array<{ label: string; prompt: string }> = [
    { label: 'Trend following',     prompt: 'A trend-following strategy using EMA crossovers, ATR-based stops, and a volatility regime filter to skip choppy markets.' },
    { label: 'Mean reversion',      prompt: 'A mean-reversion strategy using Bollinger Bands and RSI with a fixed risk-per-trade.' },
    { label: 'Momentum breakout',   prompt: 'A momentum-breakout strategy using Donchian channels and volume confirmation. Trail stops with ATR.' },
    { label: 'Range trading',       prompt: 'A range-bound oscillator strategy that fades extremes and exits at the midpoint.' },
    { label: 'Volatility breakout', prompt: 'A volatility-expansion strategy that enters after a low-volatility compression resolves into an ATR or Bollinger Band expansion.' },
    { label: 'Pullback',            prompt: 'A pullback-continuation strategy: identify trend direction, wait for a pullback to a moving average, enter on resumption signal.' },
  ];

  // P3 — Import-from-Pine lane. The model is the transpiler (convert-pine
  // edge function reuses the generator's exact validation pipeline); the
  // EVIDENCE is the gate: after import, run Backtest & Validate and use the
  // parity checker there against your TradingView trade export.
  // P9.2B — 'research' (structural_setup_v1) is the DEFAULT lane for new generation.
  // 'describe'/'pine' remain as the EXPLICIT legacy-compatibility assistant (labeled).
  const [mode, setMode] = useState<'research' | 'describe' | 'pine'>('research');
  const [pineSource, setPineSource] = useState('');
  const [conversionNotes, setConversionNotes] = useState<string[] | null>(null);
  const [unconvertible, setUnconvertible] = useState<string[] | null>(null);
  const researchIdeas = (ideaBankSummary as { ideas: Array<{ slug: string; title: string; availableDataStatus: string; operationalized: number; designs: number }> }).ideas;
  const [ideaSlug, setIdeaSlug] = useState<string>(() => researchIdeas.find((i) => i.operationalized > 0)?.slug ?? '');
  const [rejection, setRejection] = useState<{ code: string; message: string } | null>(null);

  // Typed structural rejections (P9.2B) — a rejection is an HONEST research outcome,
  // preferable to fabricated output; the copy explains what each reason means.
  const TYPED_REJECTIONS: Record<string, string> = {
    required_data_unavailable: 'This proposition requires a data surface the program does not govern yet (e.g. options or intraday history). Nothing was substituted.',
    unsupported_capability: 'A capability pack this proposition needs is not loaded on the server. Filed as a counted capability gap.',
    setup_not_compilable: 'No honest operationalized design exists for this proposition yet — the gap is filed; the idea is never distorted to force a strategy out.',
    mtf_series_unavailable: 'The multi-timeframe series this design needs is not available. Nothing was substituted.',
    semantic_complexity_exceeded: 'The design exceeded the 40-block semantic ceiling or contained redundant blocks, so it was refused.',
    duplicate_research_idea: 'Research memory rejected this as a duplicate — you already generated this economic idea (possibly under a different name/graph).',
    invalid_state_graph: 'The scenario state machine failed validation (unreachable states or bad transitions).',
    temporal_constraint_unsatisfied: 'A temporal bound in the design was contradictory or non-positive.',
    research_memory_conflict: 'Research memory was unreachable, so generation was refused rather than guessed.',
    persistence_failed: 'The strategy could not be persisted with complete lineage, so the save was refused.',
  };

  const handleResearchGenerate = async () => {
    if (!ideaSlug) { setError('Pick a research proposition first'); return; }
    setGenerating(true); setError(null); setErrorDetails(null); setRejection(null); setLastMeta(null);
    try {
      const response = await callEdgeFunction('generate-strategy', {
        research: { ideaSlug, seed: Math.floor(Date.now() % 1_000_000_000), variants: 0 },
        platform, assetClass,
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const code = String(data?.error ?? 'unknown');
        setRejection({ code, message: TYPED_REJECTIONS[code] ?? String(data?.message ?? 'Generation failed') });
        track('builder_generation_failed', { generation_mode: 'research', status: response.status, error: code });
        return;
      }
      if (data._meta) setLastMeta({ ...(data._meta as GenMeta), completedAt: Date.now(), generationMode: 'research' as GenMeta['generationMode'] });
      track('builder_generation_completed', {
        generation_mode: 'research',
        model: String(data._meta?.model ?? 'structural_setup_v1-deterministic'),
        latency_ms: Number(data._meta?.latency_ms ?? 0),
        cost_usd: 0,
        block_count: Array.isArray(data.blocks) ? data.blocks.length : 0,
      });
      onStrategyGenerated(autoLayoutBlocks(data.blocks as Block[]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate strategy');
    } finally {
      setGenerating(false);
    }
  };

  const handleConvertPine = async () => {
    if (!pineSource.trim()) {
      setError('Paste a Pine Script v5 strategy to convert');
      return;
    }
    setGenerating(true);
    setError(null);
    setErrorDetails(null);
    setConversionNotes(null);
    setUnconvertible(null);
    try {
      const response = await callEdgeFunction('convert-pine', { pineSource, platform, assetClass });
      const data = await response.json();
      if (!response.ok) {
        setError((data?.message as string) || (data?.error as string) || 'Conversion failed');
        setErrorDetails(Array.isArray(data?.violations) ? data.violations : null);
        return;
      }
      if (data._meta) setLastMeta({ ...(data._meta as GenMeta), attempts: 1, completedAt: Date.now() });
      setConversionNotes(Array.isArray(data.conversionNotes) && data.conversionNotes.length ? data.conversionNotes : null);
      setUnconvertible(Array.isArray(data.unconvertible) && data.unconvertible.length ? data.unconvertible : null);
      onStrategyGenerated(autoLayoutBlocks(data.blocks));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Conversion failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      setError('Please provide a strategy description');
      return;
    }

    setGenerating(true);
    setError(null);
    setErrorDetails(null);
    setLastMeta(null);
    // Phase 3 + hardening — the SERVER is authoritative for the AI catalog: it fetches + sanitises the
    // ai_safe blocks itself behind its own flag, so the client sends NO catalog cards. (libGate.aiCatalog
    // still gates whether we surface library-related UX; it is not the security boundary.)
    void libGate;

    // P15 — one generation attempt. Returns {ok, status, data}. A 504/502/503
    // is the gateway IDLE-TIMEOUT — distinct from a 422 invalid_output, a
    // content failure we should NOT retry.
    //
    // P15.1 — TSS attempts DISABLE THINKING. The 150s wall is cumulative
    // over the function's whole multi-attempt chain (TSS → strong-TSS retry
    // → JSON fallback) within ONE request, and the thinking pass (~6k-token
    // budget) is ~60-120s per attempt — so even small-output TSS can blow
    // the wall once a second attempt runs. The Builder is interactive (the
    // user can regenerate), so TSS trades deep design-reasoning for speed;
    // the grammar + exemplars carry the quality. JSON keeps thinking for
    // its one-shot quality, and a JSON timeout still rescues via no-thinking
    // TSS. Mirrors the optimizer's T-504 no-thinking retry. Optimizer
    // behavior is unchanged (it sets disableThinking on its OWN path).
    const attempt = async (scriptMode: boolean): Promise<{ ok: boolean; status: number; data: any }> => {
      try {
        const response = await callEdgeFunction('generate-strategy', {
          description, platform, assetClass, scriptMode,
          disableThinking: scriptMode,
          // P9.2B — the free-text assistant is the EXPLICIT legacy-compatibility lane;
          // every legacy response is labeled with its route (never silent).
          legacyRoute: { reason: 'compatibility' },
        });
        const data = await response.json().catch(() => null);
        return { ok: response.ok, status: response.status, data };
      } catch (e) {
        // Network-level abort (the client never got a response) — treat as a
        // timeout so the TSS retry can still fire.
        return { ok: false, status: 504, data: { error: 'network', message: e instanceof Error ? e.message : String(e) } };
      }
    };
    const isTimeout = (status: number) => status === 502 || status === 503 || status === 504;

    try {
      // Attempt 1: TSS if the user opted in, else JSON (the default).
      let mode: GenMeta['generationMode'] = useScript ? 'tss_manual' : 'json';
      let res = await attempt(useScript);

      // P15 — JSON timeout → ONE automatic TSS retry (TSS output is 5-10×
      // smaller, so it finishes under the 150s wall). Only when the first
      // attempt was JSON and the failure was a timeout — never on a 422
      // content failure, and never a second time.
      if (!res.ok && !useScript && isTimeout(res.status)) {
        mode = 'tss_retry_after_json_timeout';
        res = await attempt(true);
      }

      if (!res.ok) {
        const data = res.data;
        const detail =
          (data?.fallback?.errors as string[] | undefined) ??
          (data?.primary?.errors as string[] | undefined) ??
          null;
        setError(
          (isTimeout(res.status) && mode !== 'tss_retry_after_json_timeout'
            ? 'Request idle timeout limit (150s) reached'
            : (data?.message as string)) ||
          (data?.error as string) ||
          'Failed to generate strategy'
        );
        setErrorDetails(Array.isArray(detail) && detail.length ? detail : null);
        track('builder_generation_failed', {
          generation_mode: mode, status: res.status, error: String(data?.error ?? 'unknown'),
        });
        return;
      }

      const data = res.data;
      const m = (data._meta ?? {}) as Record<string, unknown>;
      if (data._meta) setLastMeta({
        ...(data._meta as GenMeta),
        completedAt: Date.now(),
        generationMode: mode,
        scriptFellBack: m.script_fell_back === true,
      });
      track('builder_generation_completed', {
        generation_mode: mode,
        model: String(m.model ?? '?'),
        latency_ms: Number(m.latency_ms ?? 0),
        cost_usd: Number(m.costUsd ?? 0),
        script_mode: m.script_mode === true,
        script_fell_back: m.script_fell_back === true,
        block_count: Array.isArray(data.blocks) ? data.blocks.length : 0,
      });
      // Phase 3 — resolve any AI-emitted library references ({type:'custom',
      // data:{libraryBlockId,libraryVersion,parameters}}) into real blocks: code
      // comes from the immutable ai_safe version, params are schema-validated,
      // provenance is stamped. Unresolvable refs (not ai_safe / disabled / bad
      // params) are dropped, never executed blind. A no-op when there are none.
      const { blocks: resolvedBlocks, dropped } = await resolveStrategyLibraryRefs(data.blocks as Block[]);
      if (dropped.length) track('ai_catalog_refs_dropped', { count: dropped.length });
      // Auto-layout: override AI-emitted positions with a deterministic
      // column-by-type layout. Defensive — preserves block ids, data, and
      // connections; ONLY mutates position. The blocks are already
      // compiled-to-TSL server-side (TSS mode) or native JSON — identical
      // shape, so the existing import/replace flow is unchanged.
      onStrategyGenerated(autoLayoutBlocks(resolvedBlocks));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate strategy'
      );
      setErrorDetails(null);
    } finally {
      setGenerating(false);
    }
  };

  // Rough cost estimate per Anthropic's listed prices (Jan 2026). Haiku 4.5:
  // \$1/M input + \$5/M output. Sonnet 4.6: \$3/M input + \$15/M output.
  // These are best-effort — for accurate billing, check the Anthropic console.
  const estimateCost = (m: GenMeta): string | null => {
    if (!m.tokens?.input || !m.tokens?.output) return null;
    const rates =
      m.model.includes('haiku') ? { in: 1, out: 5 } :
      m.model.includes('sonnet') ? { in: 3, out: 15 } :
      m.model.includes('opus') ? { in: 15, out: 75 } :
      null;
    if (!rates) return null;
    const cost = (m.tokens.input * rates.in + m.tokens.output * rates.out) / 1_000_000;
    return cost < 0.01 ? `<$0.01` : `~$${cost.toFixed(3)}`;
  };

  // Compact "x minutes ago" / "yesterday" rendering for the persisted-meta line.
  const relativeAge = (ts: number): string => {
    const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  };

  return (
    <div className="flex-1 p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <Brain className="w-6 h-6 text-purple-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-800">
              AI Strategy Builder
            </h2>
          </div>

          <p className="text-gray-600 mb-6">
            Describe your trading strategy in plain language, and our AI will
            convert it into a working strategy. Be specific about indicators,
            timeframes, and risk parameters you want to use. The AI can build
            volatility-adaptive exits (ATR stops, ATR/percentage trailing
            stops), prop-firm intraday rules (session flatten, max hold,
            cooldowns), and custom code blocks for logic the built-in
            indicators can&apos;t express.
          </p>

          {/* P3 — lane toggle: describe in NL, or import an existing Pine
              Script v5 strategy (the model converts; the backtest verifies). */}
          <div className="mb-4 inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setMode('research')}
              className={`px-4 py-1.5 ${mode === 'research' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="mode-research"
            >
              Research (structural)
            </button>
            <button
              type="button"
              onClick={() => setMode('describe')}
              className={`px-4 py-1.5 border-l border-gray-300 ${mode === 'describe' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Describe (legacy assistant)
            </button>
            <button
              type="button"
              onClick={() => setMode('pine')}
              className={`px-4 py-1.5 border-l border-gray-300 ${mode === 'pine' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Import Pine Script
            </button>
          </div>

          {mode === 'research' && (
            <div className="mb-6" data-testid="research-lane">
              <p className="text-xs text-gray-600 mb-3">
                The default generation path (<span className="font-mono">structural_setup_v1</span>):
                pick a ResearchIdeaBank proposition and the engine deterministically operationalizes
                it into a staged, state-machine strategy with thesis-derived exits and full lineage —
                no LLM, $0. A typed rejection (data gap, capability gap, duplicate) is an honest
                research outcome, not a failure to route around.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Research proposition</label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={ideaSlug}
                onChange={(e) => setIdeaSlug(e.target.value)}
                data-testid="proposition-picker"
              >
                {[...researchIdeas].sort((a, b) => (b.operationalized - a.operationalized) || a.slug.localeCompare(b.slug)).map((i) => (
                  <option key={i.slug} value={i.slug}>
                    {i.title} {i.operationalized > 0 ? `· ${i.operationalized} design${i.operationalized > 1 ? 's' : ''}` : `· ${i.availableDataStatus}`}
                  </option>
                ))}
              </select>
              {rejection && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-md text-amber-900 text-xs" data-testid="typed-rejection">
                  <p className="font-semibold font-mono mb-1">{rejection.code}</p>
                  <p>{rejection.message}</p>
                </div>
              )}
            </div>
          )}

          {mode === 'describe' && (
            <div className="mb-4 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
              Legacy free-text assistant (<span className="font-mono">legacy_block_first_v1</span>,
              compatibility lane) — output is labeled with its profile and is not the research path.
            </div>
          )}

          {mode === 'describe' ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Strategy Description
              </label>
              <textarea
                className="w-full h-32 rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: Intraday mean-reversion on BTC/USDT 5m for an FTMO challenge — RSI fade gated to low-ADX regimes, ATR-based stop with a 2R target, flatten before the US session close..."
              />
            </div>
          ) : mode === 'pine' ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pine Script v5 Source
              </label>
              <textarea
                className="w-full h-48 rounded-md border border-gray-300 px-4 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={pineSource}
                onChange={(e) => setPineSource(e.target.value)}
                placeholder={'//@version=5\nstrategy("My Strategy", ...)\n...paste the full script. Conversion runs on the strongest model and reports anything it could not faithfully reproduce. After import: run Backtest & Validate, then verify against your TradingView trade export with the parity checker.'}
              />
            </div>
          ) : null}

          {conversionNotes && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-800 text-xs">
              <p className="font-medium mb-1">Conversion notes (approximations made):</p>
              <ul className="list-disc pl-5 space-y-0.5">{conversionNotes.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </div>
          )}
          {unconvertible && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md text-yellow-800 text-xs">
              <p className="font-medium mb-1">⚠ Not reproduced (review before trusting the import):</p>
              <ul className="list-disc pl-5 space-y-0.5">{unconvertible.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </div>
          )}

          {/* Platform + Asset class dropdowns used to live here. Removed
              for the same reason they were dropped from AI Optimize: the
              AI infers asset class from the description, and Platform has
              no material differentiation. The state lives at the top of
              the component as fixed constants so the generate-strategy
              edge-function contract is unchanged. */}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{error}</p>
                  {errorDetails && errorDetails.length > 0 && (
                    <>
                      <p className="text-xs mt-2 mb-1 text-red-600">
                        Specific TSL rules the model couldn't satisfy:
                      </p>
                      <ul className="text-xs list-disc pl-5 space-y-1 max-h-48 overflow-y-auto">
                        {errorDetails.map((e, i) => (
                          <li key={i} className="font-mono break-words">{e}</li>
                        ))}
                      </ul>
                      <p className="text-xs mt-2 text-red-600">
                        Generation is non-deterministic — a retry often
                        succeeds. Simplifying the description also helps.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* P15 — opt-in TSS for from-scratch generation. JSON stays the
              default first attempt; a JSON 504 still auto-retries in TSS
              regardless of this box. Describe mode only (Pine has its own
              pipeline). */}
          {mode === 'describe' && (
            <label
              className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer mb-3"
              title="Beta: the AI writes compact TSS (Tradeatory Strategy Script), compiled server-side to the same TSL graph. Far fewer output tokens — avoids the 150s timeout on complex strategies. Leave off and a JSON timeout will still auto-retry in TSS."
            >
              <input
                type="checkbox"
                checked={useScript}
                onChange={(e) => setUseScript(e.target.checked)}
                disabled={generating}
                className="rounded border-gray-300"
              />
              Use TSS generation (beta) — recommended for complex / prop-firm strategies
            </label>
          )}

          <div className="flex items-center justify-between">
            {lastMeta ? (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 flex items-center space-x-3">
                <Cpu className="w-3 h-3 text-purple-600" />
                <span className="font-mono">{lastMeta.model}</span>
                {lastMeta.generationMode === 'tss_retry_after_json_timeout' && (
                  <span className="text-amber-700" title="JSON generation timed out; auto-retried in TSS">↻ TSS (JSON timed out)</span>
                )}
                {lastMeta.generationMode === 'tss_manual' && (
                  <span className="text-indigo-700" title="Generated via TSS (compiled to TSL)">TSS</span>
                )}
                {lastMeta.attempts > 1 && (
                  <span className="text-yellow-700">↻ {lastMeta.attempts} attempts</span>
                )}
                <span>{(lastMeta.latency_ms / 1000).toFixed(1)}s</span>
                {lastMeta.tokens?.input != null && lastMeta.tokens?.output != null && (
                  <span>{lastMeta.tokens.input}↑ {lastMeta.tokens.output}↓ tokens</span>
                )}
                {estimateCost(lastMeta) && (
                  <span className="text-green-700">{estimateCost(lastMeta)}</span>
                )}
                {lastMeta.completedAt && (
                  <span className="text-gray-400" title={new Date(lastMeta.completedAt).toLocaleString()}>
                    · {relativeAge(lastMeta.completedAt)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setLastMeta(null)}
                  className="ml-1 text-gray-400 hover:text-gray-600"
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            ) : <span />}
            <button
              onClick={mode === 'pine' ? handleConvertPine : mode === 'research' ? handleResearchGenerate : handleGenerate}
              disabled={generating}
              data-testid="generate-button"
              className={`flex items-center px-6 py-2 rounded-md font-medium transition-colors ${
                generating
                  ? 'bg-purple-400 cursor-not-allowed'
                  : mode === 'research' ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              } text-white`}
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {mode === 'pine' ? 'Converting…' : 'Generating...'}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  {mode === 'pine' ? 'Convert to Strategy' : mode === 'research' ? 'Generate Research Strategy ($0)' : 'Generate Strategy'}
                </>
              )}
            </button>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center mb-3">
            <Cpu className="w-5 h-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-medium text-purple-900">
              Quick start — strategy archetypes
            </h3>
          </div>
          <p className="text-xs text-purple-800 mb-3">
            Click an archetype to fill the description, then edit it to add your target market,
            timeframe, and risk constraints before generating.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setDescription(preset.prompt)}
                title={preset.prompt}
                className="text-xs px-2 py-1 bg-white border border-purple-200 text-purple-800 rounded hover:bg-purple-100 hover:border-purple-300 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIStrategyBuilder;
